'use client';

import { defineMessages, useIntl } from 'react-intl';
import { Price } from '@packages/shared/src/ui/Price/Price';
import { PriceValue } from '@packages/shared/src/ui/Price/PriceValue/PriceValue';
import { PriceDiscountLocalized } from '@packages/modules/src/Price/PriceDiscountLocalized/PriceDiscountLocalized';
import { priceDiscountMessages } from '@packages/modules/src/Price/PriceDiscountLocalized/messages';
import { PriceUnitPrice } from '@packages/shared/src/ui/Price/PriceUnitPrice/PriceUnitPrice';
import { PriceVoucherDiscount } from '@packages/shared/src/ui/Price/PriceVoucherDiscount/PriceVoucherDiscount';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { useFormatDiscount } from '@packages/shared/src/hooks/useFormatDiscount/useFormatDiscount';

import type { PriceProps } from '@packages/shared/src/ui/Price/Price';
import type { WishlistLineItemsFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import type { MessageValue } from 'react-intl';
import type { LineItemNormalizedPrice } from './LineItemTypes';

interface LineItemPriceProps extends PriceProps {
  price: WishlistLineItemsFragmentFragment['price'] | LineItemNormalizedPrice;
  /**
   * Rendered above the effective price, where an editable base price belongs.
   * Used by the call-center price override, which brings its own row.
   */
  priceActionSlot?: React.ReactNode;
  /** Rendered below the unit price, e.g. the call-center "price adjusted" hint. */
  hintSlot?: React.ReactNode;
}

const messages = defineMessages({
  uvp: {
    defaultMessage: 'UVP:',
    id: 'ProductPriceWrapper.uvp',
  },
  saving: {
    defaultMessage: 'statt',
    id: 'ProductPriceWrapper.saving',
  },
  startPrice: {
    id: 'price.start.from',
    defaultMessage: 'ab',
  },
});

export const LineItemPrice = ({
  alignment = 'right',
  price,
  priceActionSlot,
  hintSlot,
  ...rest
}: LineItemPriceProps) => {
  const { formatMessage, formatNumber } = useIntl();
  const { formatPrice } = useFormatPrice();
  const { formatDiscount } = useFormatDiscount();

  if (!price) {
    return null;
  }

  const { currency, saving, valueOld, uvp, unit, valueUnit, value, isStartPrice } = price;
  const voucherDiscount = 'discount' in price ? price.discount : null;
  // the checkout backend sends the deducted amount as a negative number, despite the schema's wording
  const voucherDiscountAmount = Math.abs(voucherDiscount?.discountAmount ?? 0);
  const { type: savingType, value: savingValue } = saving || {};

  let preDiscountText: string | undefined;
  if (uvp) {
    preDiscountText = formatMessage(messages.uvp);
  } else if (saving) {
    preDiscountText = formatMessage(messages.saving);
  }

  // old implementation uses saving, unsure whether discount could be used instead
  let discountText: string | undefined;
  if (!savingValue) {
    discountText = undefined;
  } else if (savingType?.toLowerCase() === 'currency') {
    discountText = `-${formatDiscount({
      priceDifference: savingValue,
      currency,
    })}`;
  } else {
    discountText = `-${formatDiscount({
      discountPercentage: savingValue,
    })}`;
  }

  return (
    <Price
      // PriceVoucherDiscount's minmax(0, 1fr) column adds nothing to the min width, so the row would squeeze it
      className="shrink-0 self-stretch whitespace-nowrap md:self-start"
      alignment={alignment}
      size="small"
      {...rest}
    >
      <>
        {valueOld && (
          <div className="mb-1">
            <PriceDiscountLocalized
              oldPrice={`${preDiscountText} ${formatPrice(valueOld, currency)}`}
              oldPriceType={uvp ? 'msrp' : undefined}
              discount={discountText}
            />
          </div>
        )}
        {/* the call-center action slot shows its own strike price and discount */}
        {!priceActionSlot && voucherDiscount && voucherDiscountAmount > 0 && (
          // PriceVoucherDiscount takes its alignment from its parent via justify-items: inherit
          <div className="mb-1 [justify-items:inherit]">
            <PriceVoucherDiscount
              allowStrikethrough
              preVoucherPrice={formatPrice(voucherDiscount.amountBeforeDiscount, currency)}
              preVoucherPriceAriaLabel={formatMessage(priceDiscountMessages.originalPrice)}
              savingValue={`-${formatDiscount({
                priceDifference: voucherDiscountAmount,
                currency,
              })}`}
            >
              {voucherDiscount.discountPercentage > 0
                ? formatMessage<{ readonly percentage: MessageValue }>(
                    {
                      id: 'LineItemPrice.voucherDiscount.percentage',
                      defaultMessage: '{percentage} Rabatt',
                    },
                    {
                      // the schema allows two decimals, which formatDiscount would round away
                      percentage: formatNumber(voucherDiscount.discountPercentage / 100, {
                        style: 'percent',
                        maximumFractionDigits: 2,
                      }),
                    },
                  )
                : formatMessage({
                    id: 'LineItemPrice.voucherDiscount',
                    defaultMessage: 'Rabatt',
                  })}
            </PriceVoucherDiscount>
          </div>
        )}
        {priceActionSlot}
        {/* a call-center agent may set a line item to 0,00, which is a price to show, not a missing one */}
        {value != null && (
          <div className="mb-1">
            <PriceValue>
              {isStartPrice && (
                <span className="text-size-body-sm mr-1">{formatMessage(messages.startPrice)}</span>
              )}
              <span className="">{formatPrice(value, currency)}</span>
            </PriceValue>
          </div>
        )}
        {unit && valueUnit && (
          <PriceUnitPrice>{`${formatPrice(valueUnit, currency)} / ${unit}`}</PriceUnitPrice>
        )}
        {hintSlot}
      </>
    </Price>
  );
};
